import React, { useState, useEffect, useRef } from "react";
import {
    Box, Card, CardContent, Typography, TextField,
    MenuItem, Button, Chip, FormControl, Select,
    InputAdornment, Divider, Slider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Radio
} from "@mui/material";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

// ✅ Google Maps
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { createCampaign } from "../../../../Component/Servicefile/campaignService";
import { getBillingConfig } from "../../../../Component/Servicefile/campaignService";
const BudgetReview = ({
    currentStep,
    formData,
    goals,
    subGoals,
    onInputChange,
    onMultiSelect,
    onNext,
    onBack,
    onPayment,
    isStepValid,
    isPaymentValid,
    isBudgetStep,
    showPayment,        // ✅ from parent
    setShowPayment,      // ✅ from parent
    adPreviewRef
}) => {
    const [durationType, setDurationType] = useState("continuous"); // continuous | fixed
    const FIXED_AMOUNT = 1000;
    // 🔥 Add this at top of component data not Founded in the real time allowing data not founded in the real time examples and the inventory categories that will usefull in the 
    const PRICE_PER_DAY = 100;
    const COST_PER_REACH = 0.5;
    const days = parseInt(formData.days) || 1;
    const reach = parseInt(formData.reach) || 1000;
    const totalPrice = (days * PRICE_PER_DAY) + (reach * COST_PER_REACH);
    const dailyCost = totalPrice / days;
    const gst = totalPrice * 0.18;
    const finalAmount = totalPrice + gst;
    const REACH_PER_RUPEE = 0.05;  // 1₹ = 0.05 users
    // ✅ Google Maps
    const baseInvestment = parseInt(formData.baseInvestment || 0);

    const INVESTMENT_STEP = 100;
    const today = new Date().toISOString().split("T")[0];
    const MAX_LIMIT = baseInvestment + 10000;
    // const [showPayment, setShowPayment] = useState(false);
    const REACH_STEP = 100;
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "AIzaSyD_peZch6hJtq-3wEE4-4z3r68o7Y7nw3c",
        libraries: ["places"]
    });
    // base reach derived from base investment
    const baseReach = Math.floor(baseInvestment / INVESTMENT_STEP) * REACH_STEP;
    // ✅ Area map states
    const [showAreaMap, setShowAreaMap] = useState(false);
    const [areaMapCenter, setAreaMapCenter] = useState({ lat: 28.6139, lng: 77.209 });
    const [billing, setBilling] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const isBrandingDigital =
        formData.goal?.toLowerCase() === "branding" &&
        formData.mediums?.includes("digital");
    const isDaysUpdate = useRef(false);


    // 1️⃣ When DAYS change → update investment only (no reach)
    useEffect(() => {
        const fetchBilling = async () => {
            try {
                const data = await getBillingConfig();

                setBilling(data);

                // ✅ SET REACH (default)
                if (!formData.reach) {
                    onInputChange("reach", data?.reach ?? 0);
                }

                // ✅ 🔥 MAIN LOGIC
                const isBrandingDigital =
                    formData.goal === "branding" &&
                    formData.mediums?.includes("digital");

                if (isBrandingDigital) {
                    const investmentValue = data?.digitalScreenCharge ?? 0;

                    onInputChange("investment", investmentValue);
                }

            } catch (err) {
                console.error("❌ Billing fetch failed", err);
            }
        };

        fetchBilling();
    }, [formData.goal, formData.mediums]); // ✅ IMPORTANT

    // 2️⃣ Calculate baseInvestment from billing — ONLY when billing/selections change
    useEffect(() => {
        if (!billing) return;

        let total = 0;
        const mediums = formData.mediums || [];
        const placements = formData.placements || [];
        const audience = formData.audience || [];
        const mediaTypes = formData.mediaTypes || [];

        if (mediaTypes.length > 0) total += billing.mediaTypeCharge * mediaTypes.length;
        if (placements.length > 0) total += billing.displaypPositionCharge * placements.length;
        if (audience.length > 0) {
            const audienceCost = billing.targetAudiencesCharge * audience.length;
            total += audienceCost;
        }
        total += billing.promotionCharge || 0;
        if (mediums.includes("digital")) {
            const screenCount = placements.length || 1;
            const pricePerScreen = billing.digitalScreenCharge || 0;

            const baseAmount = screenCount * pricePerScreen;
            const gst = baseAmount * 0.18;

            total += baseAmount + gst;  // ✅ correct calculation
        }


        // ✅ Always update baseInvestment
        onInputChange("baseInvestment", total);

        // ✅ Only set investment if not already set
        if (!formData.investment) {
            onInputChange("investment", total);
        }

    }, [billing, formData.mediums, formData.placements, formData.audience, formData.mediaTypes]);


    useEffect(() => {
        if (!formData.baseInvestment) return;

        const isBrandingDigital =
            formData.goal?.toLowerCase() === "branding" &&
            formData.mediums?.includes("digital");

        const days = Number(formData.days || 1);

        let newInvestment = 0;

        // ✅ NEW LOGIC (Digital Screen only)
        if (isBrandingDigital && billing) {
            const digitalPrice = billing?.digitalScreenCharge || 0;
            const screenCount = formData.placements?.length || 1;

            newInvestment = digitalPrice * screenCount * days;
        } else {
            // ✅ KEEP YOUR OLD LOGIC (unchanged)
            const base = Number(formData.baseInvestment || 0);
            newInvestment = base * days;
        }

        isDaysUpdate.current = true;

        if (formData.investment !== newInvestment) {
            onInputChange("investment", newInvestment);
        }

        setTimeout(() => {
            isDaysUpdate.current = false;
        }, 0);

    }, [
        formData.days,
        formData.baseInvestment,
        formData.goal,
        formData.mediums,
        formData.placements,
        billing
    ]);


    // ❌ DELETE the third useEffect that also watches formData.investment — REMOVE IT ENTIRELY

    const handlePayment = () => {
        const options = {
            key: "rzp_test_R6hte6Puir9RAR",
            amount: Math.round(finalAmount * 100),
            currency: "INR",
            name: "Maamaas",
            description: "Ad Campaign Payment",

            // ✅ FIX: make handler async and await handleSubmit
            handler: async function (response) {

                try {
                    await handleSubmit(); // ✅ AWAIT it
                    alert("✅ Payment & Campaign Created Successfully!");
                } catch (err) {
                    console.error("❌ handleSubmit failed after payment:", err);
                    alert("⚠️ Payment done but campaign creation failed: " + err.message);
                }
            },

            prefill: {
                name: "Naresh",
                email: "test@example.com",
                contact: "9999999999"
            },
            theme: { color: "#2563eb" }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
    };

    const handleSubmit = async () => {

        const customerId = localStorage.getItem("customerId");
        if (!customerId) throw new Error("Customer ID missing");

        const goal = formData?.goal?.toUpperCase();
        const discount = formData?.goalConfig?.discount || {};
        const leads = formData?.goalConfig?.leads || {};

        // ✅ DEBUG — check what's inside discount

        const startDate = discount?.startDate || leads?.startDate || formData?.startDate;
        const endDate = discount?.endDate || leads?.endDate || formData?.endDate;

        const interestMap = {
            tech: "EDUCATION",
            fashion: "OFFERS",
            sports: "ENTERTAINMENT",
            travel: "TRAVEL",
            food: "FOOD",
            beauty: "HEALTH",
            jobs: "JOBS"
        };

        const placementMap = {
            place_banner: "HOMEPAGE_BANNER",
            adds: "ADD_SCREEN",
            cart: "CHECKOUT_PAGE",
            in_app_popup: "IN_APP_POPUP"
        };

        // ✅ Check ALL possible locations where discountTarget might be stored
        const discountTarget =
            formData?.goalConfig?.discount?.discountTarget ||
            formData?.goalConfig?.discount?.discountType ||
            null;

        const selectedItems = formData?.goalConfig?.discount?.selectedItems || [];

        // ✅ Single dishId integer
        const dishId = (() => {
            if (discountTarget === "specific" && selectedItems.length > 0) {
                return selectedItems[0]?.id || selectedItems[0]?.dishId || null;
            }
            return null; // overall or no selection = null
        })();

        const payload = {
            campaignName: formData?.campaignName || formData?.name || "",
            description: formData?.description || "",
            goal: goal || "BRANDING",
            mediaType: formData?.videoFile ? "VIDEO" : "IMAGE",
            medium: formData?.mediums?.[0]?.toUpperCase() || "APP",
            customerId: customerId,

            startDate: startDate ? new Date(startDate).toISOString() : new Date().toISOString(),
            endDate: endDate ? new Date(endDate).toISOString() : new Date().toISOString(),

            city: formData?.city || "Hyderabad",
            centerLatitude: formData?.latitude || 78.5840,
            centerLongitude: formData?.longitude || 17.588,

            interests: (leads?.interests || []).length > 0
                ? leads.interests.map(i => interestMap[i?.toLowerCase()] || "FOOD")
                : ["FOOD"],

            targetAudience: (formData?.audience || []).length > 0
                ? formData.audience.map(a =>
                    a === "users" ? "Users" :
                        a === "partners" ? "Partners" :
                            a === "vendors" ? "Vendors" : a
                )
                : ["Users"],

            radiusKm: 10,
            status: "DRAFT",
            resolution: "R_720P",
            appType: "FOOD_AND_BEVERAGES",
            addDisplayPosition: placementMap[formData?.placements?.[0]] || "ADD_SCREEN",
            deepLink: formData?.deepLink || "",
            createdAt: new Date().toISOString(),

            discountPercentage: goal === "DISCOUNT" ? Number(discount?.value) || 0 : 0,
            dishId: dishId,

            // ✅ ✅ ADD THIS BLOCK (LEADS DATA)
            gender: leads?.gender ? leads.gender.toUpperCase() : null,

            minAge: leads?.ageRange?.[0] || 0,
            maxAge: leads?.ageRange?.[1] || 0,

            mobileNumber: leads?.contactMobile || ""
        };


        // ✅ File is optional
        let file = null;

        try {
            if (formData?.videoFile) {
                const res = await fetch(formData.videoFile);
                if (res.ok) {
                    const blob = await res.blob();
                    file = new File([blob], "creative.mp4", { type: blob.type || "video/mp4" });
                }
            } else if (formData?.images?.length > 0) {
                const res = await fetch(formData.images[0]);
                if (res.ok) {
                    const blob = await res.blob();
                    file = new File([blob], "creative.jpg", { type: blob.type || "image/jpeg" });
                }
            }
        } catch (fileError) {
            console.warn("⚠️ File load failed, sending without creative:", fileError.message);
            file = null;
        }

        const response = await createCampaign(payload, file);


        if (response?.status !== 200 && response?.status !== 201) {
            throw new Error("Unexpected API response: " + response?.status);
        }

        return response;
    };





    // useEffect(() => {
    //     const fetchBilling = async () => {
    //         try {
    //             const data = await getBillingConfig();
    //             console.log("💰 Billing config =>", data);
    //             setBilling(data);
    //         } catch (err) {
    //             console.error("❌ Billing fetch failed", err);
    //         }
    //     };

    //     fetchBilling();
    // }, []);


    const getCurrentAreaLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(pos => {
                setAreaMapCenter({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude
                });
            });
        }
    };

    // ================= BUDGET STEP =================
    {/* ── Budget Section ── */ }
    if (isBudgetStep) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Card elevation={3} sx={{ borderRadius: "16px" }}>
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>

                        {/* HEADER (COMMON FOR BOTH) */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>

                            {/* <Chip label="Step 4/3" color="primary" variant="outlined" /> */}
                        </Box>

                        {/* ✅ CONDITION START */}
                        {(
                            formData.goal?.toLowerCase() === "discount" ||
                            formData.subGoal?.toLowerCase() === "coupons"
                        ) ? (

                            /* ================= DISCOUNT ONLY ================= */


                            <Box sx={{ p: { xs: 2, md: 3, borderRadius: "16px", } }}>
                                <Card elevation={3} sx={{ marginTop: "-63px", borderRadius: "16px", background: "transparent", boxShadow: "none" }}>
                                    <CardContent sx={{ p: { xs: 3, md: 4, } }}>

                                        {/* ✅ MAIN ROW */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",   // ✅ MAIN FIX various data not founded in the real 
                                                gap: 3
                                            }}
                                        >

                                            {/* 🔵 Investment */}

                                            <Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                        Reach
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: "#059669" }}>
                                                        {/* ✅ formData.reach is now set on load, so always shows correctly */}
                                                        {Number(formData.reach ?? billing?.reach ?? 0).toLocaleString()} users
                                                    </Typography>
                                                </Box>

                                                <Slider
                                                    value={Number(formData.reach ?? billing?.reach ?? 0)}
                                                    min={billing?.reach ?? 0}
                                                    max={10000}
                                                    step={REACH_STEP}
                                                    onChange={(e, val) => {
                                                        onInputChange("reach", val);

                                                        const steps = (val - (billing?.reach ?? 0)) / REACH_STEP;
                                                        const newInvestment = baseInvestment + steps * INVESTMENT_STEP;
                                                        onInputChange("investment", newInvestment);
                                                    }}
                                                    sx={{ color: "#10b981" }}
                                                />
                                            </Box>
                                            {/* 🟣 Duration */}
                                            <Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                        Duration
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: "#1e40af" }}>
                                                        {days} days
                                                    </Typography>
                                                </Box>

                                                <Slider
                                                    value={days}
                                                    min={1}
                                                    max={30}
                                                    onChange={(e, val) => {
                                                        onInputChange("days", val);
                                                    }}
                                                    sx={{ color: "#8b5cf6" }}
                                                />
                                            </Box>

                                            {/* 🟢 Reach */}


                                        </Box>
                                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>

                                            {/* Start Date */}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13px",
                                                        fontWeight: 600,
                                                        color: "#374151",
                                                        mb: 0.8
                                                    }}
                                                >
                                                    Start Date
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="date"
                                                    value={formData.startDate}
                                                    onChange={e => onInputChange("startDate", e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{
                                                        min: today   // 🔥 disables past dates
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "12px",
                                                            backgroundColor: "#f8fafc"
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            {/* End Date */}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13px",
                                                        fontWeight: 600,
                                                        color: "#374151",
                                                        mb: 0.8
                                                    }}
                                                >
                                                    End Date
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="date"
                                                    value={formData.endDate}
                                                    onChange={e => onInputChange("endDate", e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{
                                                        min: formData.startDate || today  // 🔥 end date >= start date
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "12px",
                                                            backgroundColor: "#f8fafc"
                                                        }
                                                    }}
                                                />
                                            </Box>

                                        </Box>


                                        <Divider sx={{ mb: 3 }} />

                                        {/* ── Dates Section ── */}




                                        {/* ✅ Selected Locations Tags */}
                                        {formData.targetLocations?.length > 0 && (
                                            <Box sx={{
                                                p: 2, backgroundColor: "#f8fafc",
                                                border: "1px solid #e2e8f0", borderRadius: "10px", mb: 3
                                            }}>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", mb: 1.5 }}>
                                                    📍 {formData.targetLocations.length} Location(s) Selected
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {formData.targetLocations.map((loc, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={`📍 ${loc.name}`}
                                                            onDelete={() => {
                                                                const updated = formData.targetLocations.filter((_, i) => i !== idx);
                                                                onInputChange("targetLocations", updated);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "#eff6ff", color: "#1e40af",
                                                                fontWeight: 600, fontSize: "12px",
                                                                border: "1px solid #bfdbfe", borderRadius: "8px",
                                                                "& .MuiChip-deleteIcon": { color: "#3b82f6" }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}



                                        {/* ── Navigation ── */}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, }}>
                                            <Button
                                                variant="outlined" size="large"
                                                startIcon={<ArrowLeft size={18} />}
                                                onClick={onBack}
                                                sx={{
                                                    borderRadius: "10px", textTransform: "none", fontWeight: 600,
                                                    borderColor: "#e5e7eb", color: "#6b7280",
                                                    "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" }
                                                }}
                                            >
                                                Back
                                            </Button>
                                            <Button
                                                variant="contained"
                                                disabled={isSubmitted} // 🔥 disables button after click
                                                onClick={() => {
                                                    setIsSubmitted(true);
                                                    setShowPayment(true);

                                                    setTimeout(() => {
                                                        if (adPreviewRef?.current) {
                                                            const container = adPreviewRef.current;

                                                            container.scrollTo({
                                                                top: container.scrollHeight,
                                                                behavior: "smooth"
                                                            });
                                                        }
                                                    }, 100);
                                                }}
                                                sx={{
                                                    borderRadius: "10px",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    backgroundColor: isSubmitted ? "#cbd5f5" : "#f97316", // disabled color
                                                    color: "#fff",
                                                    "&:hover": {
                                                        backgroundColor: isSubmitted ? "#cbd5f5" : "#ea580c"
                                                    }
                                                }}
                                            >
                                                Submit
                                            </Button>
                                        </Box>

                                    </CardContent>
                                </Card>



                            </Box>

                        ) : formData.goal?.toLowerCase() === "branding" ? (

                            /* ================= BRANDING DATA ================= */
                            <Box sx={{ p: { xs: 2, md: 3, borderRadius: "16px", } }}>
                                <Card elevation={3} sx={{ marginTop: "-63px", borderRadius: "16px", background: "transparent", boxShadow: "none" }}>
                                    <CardContent sx={{ p: { xs: 3, md: 4, } }}>

                                        {/* ✅ MAIN ROW */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",   // ✅ MAIN FIX various data not founded in the real 
                                                gap: 3
                                            }}
                                        >
                                            {isBrandingDigital && (
                                                <Typography sx={{ fontSize: "12px", color: "#059669", mt: 1 }}>
                                                    <Box>
                                                        {/* Header */}



                                                        <Box>
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                                    Duration
                                                                </Typography>
                                                                <Typography sx={{ fontWeight: 700, color: "#1e40af" }}>
                                                                    {days} days
                                                                </Typography>
                                                            </Box>

                                                            <Slider
                                                                value={days}
                                                                min={1}
                                                                max={30}
                                                                onChange={(e, val) => {
                                                                    onInputChange("days", val);
                                                                }}
                                                                sx={{ color: "#8b5cf6" }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Typography>
                                            )}

                                            {/* 🔵 Investment */}




                                            {!isBrandingDigital && (
                                                <>

                                                    <Box>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                                Reach
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 700, color: "#059669" }}>
                                                                {/* ✅ formData.reach is now set on load, so always shows correctly */}
                                                                {Number(formData.reach ?? billing?.reach ?? 0).toLocaleString()} users
                                                            </Typography>
                                                        </Box>

                                                        <Slider
                                                            value={Number(formData.reach ?? billing?.reach ?? 0)}
                                                            min={billing?.reach ?? 0}
                                                            max={10000}
                                                            step={REACH_STEP}
                                                            onChange={(e, val) => {
                                                                onInputChange("reach", val);

                                                                const steps = (val - (billing?.reach ?? 0)) / REACH_STEP;
                                                                const newInvestment = baseInvestment + steps * INVESTMENT_STEP;
                                                                onInputChange("investment", newInvestment);
                                                            }}
                                                            sx={{ color: "#10b981" }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                                Duration
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 700, color: "#1e40af" }}>
                                                                {days} days
                                                            </Typography>
                                                        </Box>

                                                        <Slider
                                                            value={days}
                                                            min={1}
                                                            max={30}
                                                            onChange={(e, val) => {
                                                                onInputChange("days", val);
                                                            }}
                                                            sx={{ color: "#8b5cf6" }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                            {/* 🟣 Duration */}


                                            {/* 🟢 Reach */}


                                        </Box>
                                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>

                                            {/* Start Date */}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13px",
                                                        fontWeight: 600,
                                                        color: "#374151",
                                                        mb: 0.8
                                                    }}
                                                >
                                                    Start Date
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="date"
                                                    value={formData.startDate}
                                                    onChange={e => onInputChange("startDate", e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{
                                                        min: today   // 🔥 disables past dates
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "12px",
                                                            backgroundColor: "#f8fafc"
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            {/* End Date */}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13px",
                                                        fontWeight: 600,
                                                        color: "#374151",
                                                        mb: 0.8
                                                    }}
                                                >
                                                    End Date
                                                </Typography>

                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="date"
                                                    value={formData.endDate}
                                                    onChange={e => onInputChange("endDate", e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{
                                                        min: formData.startDate || today  // 🔥 end date >= start date
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "12px",
                                                            backgroundColor: "#f8fafc"
                                                        }
                                                    }}
                                                />
                                            </Box>

                                        </Box>


                                        <Divider sx={{ mb: 3 }} />

                                        {/* ── Dates Section ── */}




                                        {/* ✅ Selected Locations Tags */}
                                        {formData.targetLocations?.length > 0 && (
                                            <Box sx={{
                                                p: 2, backgroundColor: "#f8fafc",
                                                border: "1px solid #e2e8f0", borderRadius: "10px", mb: 3
                                            }}>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", mb: 1.5 }}>
                                                    📍 {formData.targetLocations.length} Location(s) Selected
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {formData.targetLocations.map((loc, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={`📍 ${loc.name}`}
                                                            onDelete={() => {
                                                                const updated = formData.targetLocations.filter((_, i) => i !== idx);
                                                                onInputChange("targetLocations", updated);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "#eff6ff", color: "#1e40af",
                                                                fontWeight: 600, fontSize: "12px",
                                                                border: "1px solid #bfdbfe", borderRadius: "8px",
                                                                "& .MuiChip-deleteIcon": { color: "#3b82f6" }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}



                                        {/* ── Navigation ── */}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, }}>
                                            <Button
                                                variant="outlined" size="large"
                                                startIcon={<ArrowLeft size={18} />}
                                                onClick={onBack}
                                                sx={{
                                                    borderRadius: "10px", textTransform: "none", fontWeight: 600,
                                                    borderColor: "#e5e7eb", color: "#6b7280",
                                                    "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" }
                                                }}
                                            >
                                                Back
                                            </Button>
                                            <Button
                                                variant="contained"
                                                disabled={isSubmitted} // 🔥 disables button after click
                                                onClick={() => {
                                                    setIsSubmitted(true);
                                                    setShowPayment(true);

                                                    setTimeout(() => {
                                                        if (adPreviewRef?.current) {
                                                            const container = adPreviewRef.current;

                                                            container.scrollTo({
                                                                top: container.scrollHeight,
                                                                behavior: "smooth"
                                                            });
                                                        }
                                                    }, 100);
                                                }}
                                                sx={{
                                                    borderRadius: "10px",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    backgroundColor: isSubmitted ? "#cbd5f5" : "#f97316", // disabled color
                                                    color: "#fff",
                                                    "&:hover": {
                                                        backgroundColor: isSubmitted ? "#cbd5f5" : "#ea580c"
                                                    }
                                                }}
                                            >
                                                Submit
                                            </Button>
                                        </Box>

                                    </CardContent>
                                </Card>



                            </Box>

                        ) : (

                            /* ================= NORMAL (LEADS Data code.) ==================== Various data code also not used in this particular producion of the complined values  */
                            <>
                                <Box sx={{ p: { xs: 2, md: 3, borderRadius: "16px", } }}>
                                    <Card elevation={3} sx={{ marginTop: "-63px", borderRadius: "16px", background: "transparent", boxShadow: "none" }}>
                                        <CardContent sx={{ p: { xs: 3, md: 4, } }}>

                                            {/* ✅ MAIN ROW */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",   // ✅ MAIN FIX various data not founded in the real 
                                                    gap: 3
                                                }}
                                            >
                                                <Box>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                            Reach
                                                        </Typography>
                                                        <Typography sx={{ fontWeight: 700, color: "#059669" }}>
                                                            {/* ✅ formData.reach is now set on load, so always shows correctly */}
                                                            {Number(formData.reach ?? billing?.reach ?? 0).toLocaleString()} users
                                                        </Typography>
                                                    </Box>

                                                    <Slider
                                                        value={Number(formData.reach ?? billing?.reach ?? 0)}
                                                        min={billing?.reach ?? 0}
                                                        max={10000}
                                                        step={REACH_STEP}
                                                        onChange={(e, val) => {
                                                            onInputChange("reach", val);
                                                            const steps = (val - (billing?.reach ?? 0)) / REACH_STEP;
                                                            const newInvestment = baseInvestment + steps * INVESTMENT_STEP;
                                                            onInputChange("investment", newInvestment);
                                                        }}
                                                        sx={{ color: "#10b981" }}
                                                    />
                                                </Box>
                                                {/* 🟣 Duration */}
                                                <Box>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                            Duration
                                                        </Typography>
                                                        <Typography sx={{ fontWeight: 700, color: "#1e40af" }}>
                                                            {days} days
                                                        </Typography>
                                                    </Box>

                                                    <Slider
                                                        value={days}
                                                        min={1}
                                                        max={30}
                                                        onChange={(e, val) => {
                                                            onInputChange("days", val);
                                                        }}
                                                        sx={{ color: "#8b5cf6" }}
                                                    />
                                                </Box>

                                            </Box>
                                            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>

                                                {/* Start Date */}
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "13px",
                                                            fontWeight: 600,
                                                            color: "#374151",
                                                            mb: 0.8
                                                        }}
                                                    >
                                                        Start Date
                                                    </Typography>

                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        value={formData.startDate}
                                                        onChange={e => onInputChange("startDate", e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{
                                                            min: today   // 🔥 disables past dates
                                                        }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "12px",
                                                                backgroundColor: "#f8fafc"
                                                            }
                                                        }}
                                                    />
                                                </Box>

                                                {/* End Date */}
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "13px",
                                                            fontWeight: 600,
                                                            color: "#374151",
                                                            mb: 0.8
                                                        }}
                                                    >
                                                        End Date
                                                    </Typography>

                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        value={formData.endDate}
                                                        onChange={e => onInputChange("endDate", e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{
                                                            min: formData.startDate || today  // 🔥 end date >= start date
                                                        }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "12px",
                                                                backgroundColor: "#f8fafc"
                                                            }
                                                        }}
                                                    />
                                                </Box>

                                            </Box>

                                            <Divider sx={{ mb: 3 }} />

                                            {/* ── Dates Section ── */}




                                            {/* ✅ Selected Locations Tags */}
                                            {formData.targetLocations?.length > 0 && (
                                                <Box sx={{
                                                    p: 2, backgroundColor: "#f8fafc",
                                                    border: "1px solid #e2e8f0", borderRadius: "10px", mb: 3
                                                }}>
                                                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", mb: 1.5 }}>
                                                        📍 {formData.targetLocations.length} Location(s) Selected
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {formData.targetLocations.map((loc, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={`📍 ${loc.name}`}
                                                                onDelete={() => {
                                                                    const updated = formData.targetLocations.filter((_, i) => i !== idx);
                                                                    onInputChange("targetLocations", updated);
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: "#eff6ff", color: "#1e40af",
                                                                    fontWeight: 600, fontSize: "12px",
                                                                    border: "1px solid #bfdbfe", borderRadius: "8px",
                                                                    "& .MuiChip-deleteIcon": { color: "#3b82f6" }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}



                                            {/* ── Navigation ── */}
                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, }}>
                                                <Button
                                                    variant="outlined" size="large"
                                                    startIcon={<ArrowLeft size={18} />}
                                                    onClick={onBack}
                                                    sx={{
                                                        borderRadius: "10px", textTransform: "none", fontWeight: 600,
                                                        borderColor: "#e5e7eb", color: "#6b7280",
                                                        "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" }
                                                    }}
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    disabled={isSubmitted} // 🔥 disables button after click
                                                    onClick={() => {
                                                        setIsSubmitted(true);
                                                        setShowPayment(true);

                                                        setTimeout(() => {
                                                            if (adPreviewRef?.current) {
                                                                const container = adPreviewRef.current;

                                                                container.scrollTo({
                                                                    top: container.scrollHeight,
                                                                    behavior: "smooth"
                                                                });
                                                            }
                                                        }, 100);
                                                    }}
                                                    sx={{
                                                        borderRadius: "10px",
                                                        textTransform: "none",
                                                        fontWeight: 600,
                                                        backgroundColor: isSubmitted ? "#cbd5f5" : "#f97316", // disabled color
                                                        color: "#fff",
                                                        "&:hover": {
                                                            backgroundColor: isSubmitted ? "#cbd5f5" : "#ea580c"
                                                        }
                                                    }}
                                                >
                                                    Submit
                                                </Button>
                                            </Box>

                                        </CardContent>
                                    </Card>



                                </Box>

                            </>

                        )
                        }
                        {/* ✅ CONDITION END */}

                    </CardContent >
                </Card >
            </Box >
        );
    }

    // ================= REVIEW STEP =================


};

export default BudgetReview;